import React, { useRef, useEffect, useState } from 'react';
import './EmotionalTimeline.css';

const MOOD_COLORS = {
  overwhelmed: '#EF4444',
  sad: '#8B5CF6',
  anxious: '#F59E0B',
  neutral: '#6B7280',
  calm: '#3B82F6',
  happy: '#10B981'
};

const MOOD_SCORES = {
  overwhelmed: 1,
  sad: 2,
  anxious: 3,
  neutral: 4,
  calm: 5,
  happy: 6
};

function EmotionalTimeline({ data }) {
  const canvasRef = useRef(null);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!data || !data.dataPoints || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    
    drawChart(ctx, data, rect.width, rect.height);
  }, [data]);

  const drawChart = (ctx, chartData, width, height) => {
    ctx.clearRect(0, 0, width, height);
    
    const padding = { top: 40, right: 40, bottom: 60, left: 60 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    
    const dataPoints = chartData.dataPoints;
    const smoothedData = chartData.smoothedData;
    
    // Draw grid lines
    drawGrid(ctx, padding, chartWidth, chartHeight);
    
    // Draw axes
    drawAxes(ctx, padding, chartWidth, chartHeight, dataPoints);
    
    // Draw trend line
    if (chartData.trendLine) {
      drawTrendLine(ctx, padding, chartWidth, chartHeight, dataPoints, chartData.trendLine);
    }
    
    // Draw smoothed area
    if (smoothedData && smoothedData.length > 0) {
      drawSmoothedArea(ctx, padding, chartWidth, chartHeight, smoothedData);
    }
    
    // Draw main line
    drawMainLine(ctx, padding, chartWidth, chartHeight, dataPoints);
    
    // Draw data points
    drawDataPoints(ctx, padding, chartWidth, chartHeight, dataPoints);
  };

  const drawGrid = (ctx, padding, chartWidth, chartHeight) => {
    ctx.strokeStyle = '#E5E7EB';
    ctx.lineWidth = 1;
    
    // Horizontal grid lines (mood scores)
    for (let i = 0; i <= 6; i++) {
      const y = padding.top + chartHeight - (i / 6) * chartHeight;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(padding.left + chartWidth, y);
      ctx.stroke();
    }
    
    // Vertical grid lines (time)
    const gridLines = 6;
    for (let i = 0; i <= gridLines; i++) {
      const x = padding.left + (i / gridLines) * chartWidth;
      ctx.beginPath();
      ctx.moveTo(x, padding.top);
      ctx.lineTo(x, padding.top + chartHeight);
      ctx.stroke();
    }
  };

  const drawAxes = (ctx, padding, chartWidth, chartHeight, dataPoints) => {
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 2;
    
    // Y-axis
    ctx.beginPath();
    ctx.moveTo(padding.left, padding.top);
    ctx.lineTo(padding.left, padding.top + chartHeight);
    ctx.stroke();
    
    // X-axis
    ctx.beginPath();
    ctx.moveTo(padding.left, padding.top + chartHeight);
    ctx.lineTo(padding.left + chartWidth, padding.top + chartHeight);
    ctx.stroke();
    
    // Y-axis labels (mood scores)
    ctx.fillStyle = '#6B7280';
    ctx.font = '12px Inter, system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    
    const moodLabels = ['Overwhelmed', 'Sad', 'Anxious', 'Neutral', 'Calm', 'Happy'];
    for (let i = 0; i <= 6; i++) {
      const y = padding.top + chartHeight - (i / 6) * chartHeight;
      if (i > 0 && i < 6) {
        ctx.fillText(moodLabels[i], padding.left - 10, y);
      }
    }
    
    // X-axis labels (dates)
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    
    const labelInterval = Math.floor(dataPoints.length / 6);
    dataPoints.forEach((point, index) => {
      if (index % labelInterval === 0 || index === dataPoints.length - 1) {
        const x = padding.left + (index / (dataPoints.length - 1)) * chartWidth;
        const date = new Date(point.date);
        const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        ctx.fillText(label, x, padding.top + chartHeight + 10);
      }
    });
  };

  const drawTrendLine = (ctx, padding, chartWidth, chartHeight, dataPoints, trendLine) => {
    ctx.strokeStyle = '#9CA3AF';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    
    const startY = padding.top + chartHeight - (trendLine.slope * 0 + parseFloat(trendLine.equation.split('+')[1]) / 6) * chartHeight;
    const endY = padding.top + chartHeight - (trendLine.slope * (dataPoints.length - 1) + parseFloat(trendLine.equation.split('+')[1]) / 6) * chartHeight;
    
    ctx.beginPath();
    ctx.moveTo(padding.left, startY);
    ctx.lineTo(padding.left + chartWidth, endY);
    ctx.stroke();
    
    ctx.setLineDash([]);
  };

  const drawSmoothedArea = (ctx, padding, chartWidth, chartHeight, smoothedData) => {
    if (smoothedData.length < 2) return;
    
    const gradient = ctx.createLinearGradient(0, padding.top, 0, padding.top + chartHeight);
    gradient.addColorStop(0, 'rgba(16, 185, 129, 0.2)');
    gradient.addColorStop(1, 'rgba(16, 185, 129, 0.0)');
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    
    smoothedData.forEach((point, index) => {
      const x = padding.left + (index / (smoothedData.length - 1)) * chartWidth;
      const y = padding.top + chartHeight - (point.avgScore / 6) * chartHeight;
      
      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    
    // Close the path to the bottom
    ctx.lineTo(padding.left + chartWidth, padding.top + chartHeight);
    ctx.lineTo(padding.left, padding.top + chartHeight);
    ctx.closePath();
    ctx.fill();
  };

  const drawMainLine = (ctx, padding, chartWidth, chartHeight, dataPoints) => {
    if (dataPoints.length < 2) return;
    
    ctx.strokeStyle = '#10B981';
    ctx.lineWidth = 3;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    
    ctx.beginPath();
    
    dataPoints.forEach((point, index) => {
      if (!point.hasEntry) return;
      
      const x = padding.left + (index / (dataPoints.length - 1)) * chartWidth;
      const y = padding.top + chartHeight - (point.moodScore / 6) * chartHeight;
      
      if (index === 0 || !dataPoints[index - 1].hasEntry) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    
    ctx.stroke();
  };

  const drawDataPoints = (ctx, padding, chartWidth, chartHeight, dataPoints) => {
    dataPoints.forEach((point, index) => {
      if (!point.hasEntry) return;
      
      const x = padding.left + (index / (dataPoints.length - 1)) * chartWidth;
      const y = padding.top + chartHeight - (point.moodScore / 6) * chartHeight;
      
      // Outer circle (white border)
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, Math.PI * 2);
      ctx.fill();
      
      // Inner circle (mood color)
      ctx.fillStyle = MOOD_COLORS[point.mood] || '#10B981';
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fill();
    });
  };

  const handleMouseMove = (e) => {
    if (!data || !data.dataPoints || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const padding = { top: 40, right: 40, bottom: 60, left: 60 };
    const chartWidth = rect.width - padding.left - padding.right;
    
    // Find nearest data point
    const dataPoints = data.dataPoints;
    let nearestPoint = null;
    let minDistance = Infinity;
    
    dataPoints.forEach((point, index) => {
      if (!point.hasEntry) return;
      
      const pointX = padding.left + (index / (dataPoints.length - 1)) * chartWidth;
      const distance = Math.abs(x - pointX);
      
      if (distance < minDistance && distance < 20) {
        minDistance = distance;
        nearestPoint = { ...point, index };
      }
    });
    
    if (nearestPoint) {
      setHoveredPoint(nearestPoint);
      setTooltipPos({ x: e.clientX, y: e.clientY });
    } else {
      setHoveredPoint(null);
    }
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
  };

  return (
    <div className="emotional-timeline">
      <div className="emotional-timeline__header">
        <h3 className="emotional-timeline__title">Emotional Journey</h3>
        {data?.trendLine && (
          <div className="emotional-timeline__trend">
            <span className={`emotional-timeline__trend-badge emotional-timeline__trend-badge--${data.trendLine.direction || 'stable'}`}>
              {data.trendLine.direction === 'improving' && '↗'}
              {data.trendLine.direction === 'declining' && '↘'}
              {data.trendLine.direction === 'stable' && '→'}
              <span className="emotional-timeline__trend-text">
                {data.trendLine.direction || 'stable'}
              </span>
            </span>
          </div>
        )}
      </div>
      
      <div className="emotional-timeline__canvas-wrapper">
        <canvas
          ref={canvasRef}
          className="emotional-timeline__canvas"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />
        
        {hoveredPoint && (
          <div 
            className="emotional-timeline__tooltip"
            style={{
              left: `${tooltipPos.x}px`,
              top: `${tooltipPos.y - 80}px`
            }}
          >
            <div className="emotional-timeline__tooltip-date">
              {new Date(hoveredPoint.date).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </div>
            <div className="emotional-timeline__tooltip-mood">
              <span 
                className="emotional-timeline__tooltip-mood-dot"
                style={{ backgroundColor: MOOD_COLORS[hoveredPoint.mood] }}
              />
              <span className="emotional-timeline__tooltip-mood-label">
                {hoveredPoint.mood.charAt(0).toUpperCase() + hoveredPoint.mood.slice(1)}
              </span>
            </div>
            <div className="emotional-timeline__tooltip-score">
              Score: {hoveredPoint.moodScore.toFixed(1)}/6
            </div>
            {hoveredPoint.entryCount > 1 && (
              <div className="emotional-timeline__tooltip-count">
                {hoveredPoint.entryCount} entries
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="emotional-timeline__legend">
        {Object.entries(MOOD_COLORS).map(([mood, color]) => (
          <div key={mood} className="emotional-timeline__legend-item">
            <span 
              className="emotional-timeline__legend-dot"
              style={{ backgroundColor: color }}
            />
            <span className="emotional-timeline__legend-label">
              {mood.charAt(0).toUpperCase() + mood.slice(1)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default EmotionalTimeline;
